import { useEffect, useRef, useState, type FormEvent } from "react";
import { hieraApi } from "../lib/api";
import { t, type Lang } from "../lib/i18n";
import { canAdminRole, canEditRole } from "../lib/roles";

type Tab = "settings" | "members" | "security";
type Member = { userId: string; displayName: string; role: string };
type Hit = { id: string; displayName: string };
type Tpl = {
  id: string;
  name: string;
  nameRu?: string;
  description?: string;
  descriptionRu?: string;
  pageCount?: number;
  builtin?: boolean;
};

type ShareRow = {
  id: string;
  token: string;
  page_id: string;
  page_title: string;
  created_at: string;
  expires_at: string;
  created_by_name: string;
};

type Props = {
  lang: Lang;
  spaceId: string;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
};

const FREQUENT = ["📚", "📖", "📝", "💡", "🔖", "📁", "⭐", "🔥", "🌙", "🏠", "🚀", "🔒", "🌿", "📜", "🗺️", "✨", "📌", "🎯", "🧠", "🗝️"];

function letters(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  const a = words[0]?.[0] || "";
  const b = words[1]?.[0] || words[0]?.[1] || "";
  return (a + b).toUpperCase() || "·";
}

function roleLabel(lang: Lang, role: string): string {
  if (role === "founder") return t(lang, "spaceFounder");
  if (role === "admin") return t(lang, "spaceRoleFull");
  if (role === "moderator") return t(lang, "spaceRoleEdit");
  return t(lang, "spaceRoleView");
}

export function SpaceSettings({ lang, spaceId, onClose, onChanged, onDeleted }: Props) {
  const [tab, setTab] = useState<Tab>("settings");
  const [admin, setAdmin] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [savedSlug, setSavedSlug] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [iconOpen, setIconOpen] = useState(false);
  const [shareOff, setShareOff] = useState(false);
  const [viewerComments, setViewerComments] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState<Tpl[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [tplBusy, setTplBusy] = useState(false);
  const [tplNote, setTplNote] = useState("");
  const [shares, setShares] = useState<ShareRow[]>([]);
  const [sharesPending, setSharesPending] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  async function loadShares() {
    try {
      const res = await hieraApi.spaceShares(spaceId);
      setShares(res.shares);
      setSharesPending(false);
    } catch (err) {
      setShares([]);
      setSharesPending((err as Error).message === "schema_pending");
    }
  }

  useEffect(() => {
    let dead = false;
    hieraApi.space(spaceId).then((res) => {
      if (dead) return;
      const space = res.space;
      setAdmin(canAdminRole(res.role));
      setCanEdit(canEditRole(res.role));
      setTitle(String(space.title || ""));
      setSlug(String(space.slug || ""));
      setSavedSlug(String(space.slug || ""));
      setDescription(String(space.description || ""));
      setIcon(String(space.icon || ""));
      setShareOff(Boolean(space.share_disabled));
      setViewerComments(Boolean(space.viewer_comments));
    });
    hieraApi.members(spaceId).then((res) => {
      if (!dead) setMembers(res.members);
    });
    hieraApi.templates().then((res) => {
      if (dead) return;
      setTemplates(res.templates);
      if (res.templates[0] && !templateId) setTemplateId(res.templates[0].id);
    }).catch(() => undefined);
    return () => {
      dead = true;
    };
  }, [spaceId]);

  useEffect(() => {
    if (!iconOpen) return;
    function onPointer(e: MouseEvent) {
      if (!iconRef.current?.contains(e.target as Node)) setIconOpen(false);
    }
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [iconOpen]);

  useEffect(() => {
    const text = q.trim();
    if (!admin || text.length < 1) {
      setHits([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void hieraApi.searchUsers(spaceId, text).then((res) => setHits(res.users));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [q, admin, spaceId]);

  useEffect(() => {
    if (tab !== "security") return;
    void loadShares();
  }, [tab, spaceId]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await hieraApi.patchSpace(spaceId, { title: title.trim(), slug: slug.trim(), description, icon });
      onChanged();
      onClose();
    } catch (err) {
      const code = (err as Error).message;
      if (code === "slug_exists") setError(t(lang, "slugExists"));
      else if (code === "schema_pending") setError(t(lang, "schemaPending"));
    }
  }

  async function toggle(flag: "share" | "comments", on: boolean) {
    setError("");
    if (flag === "share") setShareOff(on);
    else setViewerComments(on);
    try {
      await hieraApi.patchSpace(spaceId, flag === "share" ? { shareDisabled: on } : { viewerComments: on });
      onChanged();
    } catch (err) {
      if (flag === "share") setShareOff(!on);
      else setViewerComments(!on);
      if ((err as Error).message === "schema_pending") setError(t(lang, "schemaPending"));
    }
  }

  async function setRole(userId: string, role: string) {
    await hieraApi.addMember(spaceId, { userId, role });
    setMembers((rows) => rows.map((row) => (row.userId === userId ? { ...row, role } : row)));
  }

  async function add(user: Hit) {
    await hieraApi.addMember(spaceId, { userId: user.id, role: "observer" });
    setMembers((rows) => [...rows, { userId: user.id, displayName: user.displayName, role: "observer" }]);
    setHits((rows) => rows.filter((row) => row.id !== user.id));
    setQ("");
  }

  async function remove(userId: string) {
    await hieraApi.removeMember(spaceId, userId);
    setMembers((rows) => rows.filter((row) => row.userId !== userId));
  }

  async function destroy() {
    if (confirm !== title) return;
    await hieraApi.deleteSpace(spaceId, title);
    onDeleted();
  }

  function tplTitle(row: Tpl): string {
    return lang === "ru" ? (row.nameRu || row.name) : row.name;
  }

  function tplDesc(row: Tpl): string {
    return lang === "ru" ? (row.descriptionRu || row.description || "") : (row.description || "");
  }

  async function applyTpl() {
    if (!templateId || !canEdit) return;
    if (!window.confirm(t(lang, "applyTemplateConfirm"))) return;
    setTplBusy(true);
    setTplNote("");
    setError("");
    try {
      const res = await hieraApi.applyTemplate(spaceId, templateId);
      setTplNote(`${t(lang, "applyTemplateOk")}: ${res.pageCount} ${t(lang, "templatePages")}`);
      onChanged();
    } catch {
      setError(t(lang, "applyTemplateFail"));
    } finally {
      setTplBusy(false);
    }
  }

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal" role="dialog" aria-label={t(lang, "spaceSettings")} onMouseDown={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <strong>{t(lang, "spaceSettings")}</strong>
          <button type="button" onClick={onClose} aria-label="×">×</button>
        </header>
        <div className="modal-tabs">
          <button type="button" className={tab === "settings" ? "is-on" : ""} onClick={() => setTab("settings")}>
            {t(lang, "settings")}
          </button>
          <button type="button" className={tab === "members" ? "is-on" : ""} onClick={() => setTab("members")}>
            {t(lang, "members")}
          </button>
          <button type="button" className={tab === "security" ? "is-on" : ""} onClick={() => setTab("security")}>
            {t(lang, "spaceSecurity")}
          </button>
        </div>

        {tab === "settings" ? (
          <form className="modal-body account-form" onSubmit={(e) => void save(e)}>
            <div className="icon-field" ref={iconRef}>
              <button
                type="button"
                className="space-mark"
                disabled={!admin}
                aria-label={t(lang, "spaceIcon")}
                onClick={() => setIconOpen((open) => !open)}
              >
                {icon.trim() || letters(title)}
              </button>
              {iconOpen && admin ? (
                <div className="icon-pop">
                  <div className="emoji-grid">
                    {FREQUENT.map((emoji) => (
                      <button key={emoji} type="button" onClick={() => { setIcon(emoji); setIconOpen(false); }}>
                        {emoji}
                      </button>
                    ))}
                  </div>
                  <input
                    value={icon}
                    maxLength={8}
                    aria-label={t(lang, "spaceIcon")}
                    onChange={(e) => setIcon(e.target.value)}
                  />
                  <button type="button" className="emoji-clear" onClick={() => { setIcon(""); setIconOpen(false); }}>
                    {t(lang, "clearEmoji")}
                  </button>
                </div>
              ) : null}
            </div>
            <label>
              {t(lang, "name")}
              <input value={title} maxLength={200} required disabled={!admin} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label>
              {t(lang, "spaceSlug")}
              <input value={slug} maxLength={64} required disabled={!admin} onChange={(e) => setSlug(e.target.value)} />
              {admin && slug.trim() !== savedSlug ? (
                <span className="field-warn">{t(lang, "spaceSlugWarn").replace("{slug}", savedSlug)}</span>
              ) : null}
            </label>
            <label>
              {t(lang, "spaceDescription")}
              <textarea value={description} maxLength={4000} disabled={!admin} onChange={(e) => setDescription(e.target.value)} />
            </label>
            {error ? <p className="muted">{error}</p> : null}
            <div className="account-split">
              {admin ? <button type="submit" className="btn-gold">{t(lang, "save")}</button> : null}
              <button
                type="button"
                className="btn-line"
                onClick={() => void hieraApi.exportSpace(spaceId, `${slug || "space"}.zip`)}
              >
                {t(lang, "spaceExport")}
              </button>
            </div>
            {canEdit && templates.length ? (
              <section className="account-block">
                <h2>{t(lang, "applyTemplate")}</h2>
                <p className="muted">{t(lang, "applyTemplateHint")}</p>
                <label>
                  {t(lang, "pickTemplate")}
                  <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={tplBusy}>
                    {templates.map((row) => (
                      <option key={row.id} value={row.id}>
                        {tplTitle(row)}
                        {row.pageCount ? ` · ${row.pageCount}` : ""}
                        {row.builtin ? ` (${t(lang, "templateBuiltin")})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
                {templateId ? (
                  <p className="muted">{tplDesc(templates.find((row) => row.id === templateId) || templates[0])}</p>
                ) : null}
                <button type="button" className="btn" disabled={tplBusy || !templateId} onClick={() => void applyTpl()}>
                  {t(lang, "applyTemplate")}
                </button>
                {tplNote ? <p className="muted">{tplNote}</p> : null}
              </section>
            ) : null}
            {admin ? (
              <section className="account-block">
                <h2>{t(lang, "spaceDelete")}</h2>
                <p className="muted">{t(lang, "spaceDeleteHint")}</p>
                <input value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                <button type="button" className="btn-danger" disabled={confirm !== title} onClick={() => void destroy()}>
                  {t(lang, "spaceDelete")}
                </button>
              </section>
            ) : null}
          </form>
        ) : null}

        {tab === "members" ? (
          <div className="modal-body">
            {admin ? (
              <label className="account-form">
                {t(lang, "spaceFind")}
                <input value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
            ) : null}
            {hits.length ? (
              <ul className="people-list">
                {hits.map((user) => (
                  <li key={user.id}>
                    <strong>{user.displayName}</strong>
                    <button type="button" className="btn-line" onClick={() => void add(user)}>
                      {t(lang, "spaceAdd")}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <ul className="people-list">
              {members.map((member) => (
                <li key={member.userId}>
                  <span>
                    <strong>{member.displayName}</strong>
                    {member.role === "founder" ? <span className="meta">{roleLabel(lang, member.role)}</span> : null}
                  </span>
                  {member.role === "founder" || !admin ? (
                    member.role === "founder" ? null : <span className="meta">{roleLabel(lang, member.role)}</span>
                  ) : (
                    <span className="account-split">
                      <select value={member.role} onChange={(e) => void setRole(member.userId, e.target.value)}>
                        <option value="admin">{t(lang, "spaceRoleFull")}</option>
                        <option value="moderator">{t(lang, "spaceRoleEdit")}</option>
                        <option value="observer">{t(lang, "spaceRoleView")}</option>
                      </select>
                      <button type="button" className="btn-line" onClick={() => void remove(member.userId)}>
                        {t(lang, "remove")}
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {tab === "security" ? (
          <div className="modal-body">
            {error ? <p className="muted">{error}</p> : null}
            <p className="muted space-private-hint">{t(lang, "spacePrivateHint")}</p>
            <div className="set-row">
              <div>
                <strong>{t(lang, "spaceShareOff")}</strong>
                <p>{t(lang, "spaceShareOffHint")}</p>
              </div>
              <button
                type="button"
                className={shareOff ? "switch is-on" : "switch"}
                role="switch"
                aria-checked={shareOff}
                disabled={!admin}
                onClick={() => void toggle("share", !shareOff)}
              >
                <span />
              </button>
            </div>
            <section className="account-block space-shares">
              <h2>{t(lang, "spaceShares")}</h2>
              <p className="muted">{t(lang, "spaceSharesHint")}</p>
              {sharesPending ? <p className="muted">{t(lang, "spaceSharesPending")}</p> : null}
              {!sharesPending && !shares.length ? <p className="muted">{t(lang, "spaceSharesEmpty")}</p> : null}
              {shares.length ? (
                <ul className="share-list">
                  {shares.map((row) => (
                    <li key={row.id}>
                      <div>
                        <strong>{row.page_title || "Untitled"}</strong>
                        <p className="meta">
                          {t(lang, "spaceShareBy")} {row.created_by_name || "—"}
                          {" · "}
                          {new Date(row.created_at).toLocaleString(lang === "ru" ? "ru-RU" : "en-GB")}
                          {" · "}
                          {t(lang, "spaceShareExpires")}{" "}
                          {new Date(row.expires_at).toLocaleString(lang === "ru" ? "ru-RU" : "en-GB")}
                        </p>
                      </div>
                      {canEdit ? (
                        <button
                          type="button"
                          className="btn-line"
                          onClick={() => {
                            void hieraApi.revokeShare(spaceId, row.id).then(() => loadShares());
                          }}
                        >
                          {t(lang, "spaceShareRevoke")}
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
            <div className="set-row">
              <div>
                <strong>
                  {t(lang, "spaceViewerComments")} <em className="set-badge">{t(lang, "soon")}</em>
                </strong>
                <p>{t(lang, "spaceViewerCommentsHint")}</p>
              </div>
              <button
                type="button"
                className={viewerComments ? "switch is-on" : "switch"}
                role="switch"
                aria-checked={viewerComments}
                disabled
                onClick={() => void toggle("comments", !viewerComments)}
              >
                <span />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
